import jsPDF from "jspdf";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import dayjs from "dayjs";

// ─── helpers ────────────────────────────────────────────────
const str = (v) => (v === null || v === undefined ? "" : String(v));

const formatMoney = (value) => {
  if (!value && value !== 0) return "0.00";
  return Number(value).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateStr) =>
  dateStr ? dayjs(dateStr).format("DD/MM/YYYY") : "-";

// สีอ้างอิงจาก THEME ของหน้า DuePaymentTab เพื่อให้เอกสารพิมพ์ตรงกับที่เห็นบนหน้าจอ
const COLOR = {
  success: [39, 174, 96],
  warning: [243, 156, 18],
  warningDark: [180, 95, 6],
  danger: [231, 76, 60],
  info: [52, 152, 219],
  dark: [30, 41, 59],
  gray: [140, 140, 140],
};

// สถานะงวดดิว: อ้างอิง logic เดียวกับ handleDueDate ในหน้า DuePaymentTab
const getDueStatus = (record) => {
  const today = dayjs();
  const dueDate = dayjs(record.DUEDATE);
  const hasPaydate = record.PAYDATE && dayjs(record.PAYDATE).isValid();
  const useDate = hasPaydate ? dayjs(record.PAYDATE) : today;
  const isOverdue = dueDate.isBefore(useDate, "day");
  const diffDays = useDate.diff(dueDate, "day");

  if (isOverdue) return { text: `เลยกำหนด ${diffDays} วัน`, color: COLOR.danger };
  if (hasPaydate) return { text: "ปกติ", color: COLOR.success };
  return { text: "รอชำระ", color: COLOR.gray };
};

// สีตัวเลขเข้าต้น/เข้าดอก/ค่างวด: อ้างอิง logic เดียวกับ handleDueAmt ในหน้า DuePaymentTab
const getDueAmtColor = (record) => {
  const today = dayjs();
  const dueDate = dayjs(record.DUEDATE);
  const dueAmt = Number(record.DUEAMT || 0);
  const payAmt = Number(record.PAYAMT || 0);
  const isFullyPaid = payAmt > 0 && payAmt >= dueAmt;
  const isPartiallyPaid = payAmt > 0 && payAmt < dueAmt;
  const isOverdue = dueDate.isBefore(today, "day");

  if (isFullyPaid) return COLOR.success;
  if (isPartiallyPaid) return COLOR.info;
  if (isOverdue) return COLOR.danger;
  return COLOR.dark;
};

// ─── main export ────────────────────────────────────────────
// rows: ผลลัพธ์จาก mergeDuePaymentWithPenalty — แถวปกติ (__rowType "due")
// สลับกับแถวเบี้ยปรับ (__rowType "penalty") ที่แทรกอยู่ถัดจากงวดที่เกี่ยวข้อง
export const exportDuePaymentPdf = async ({
  contno,
  customerName,
  rows,
  pdf: existingPdf, // ส่ง jsPDF เดิมมาเพื่อต่อหน้าในไฟล์เดียวกัน
  openWindow = true, // false = ไม่เปิดหน้าต่างพิมพ์ (คืนค่า pdf กลับไป)
}) => {
  if (!rows?.length) return existingPdf;

  const pdf =
    existingPdf ||
    new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  if (existingPdf) pdf.addPage("a4", "portrait");
  const setF = (style = "normal", size = 12) => {
    pdf.setFont("THSarabunNew", style);
    pdf.setFontSize(size);
  };
  const black = () => pdf.setTextColor(0, 0, 0);

  const pageW = 210;
  const pageH = 297;
  const mL = 14;
  const mR = 14;
  const xR = pageW - mR;
  const contentW = xR - mL;
  const bottomLimit = pageH - 16;

  const box = (x, y, w, h) => {
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.2);
    pdf.rect(x, y, w, h, "S");
  };

  // ── header (ซ้ำทุกหน้า) ── คอลัมน์ตรงกับตารางในหน้า DuePaymentTab
  const headers = [
    "งวดที่",
    "วันครบกำหนด",
    "วันที่ชำระ",
    "สถานะ",
    "เงินต้นคงเหลือ",
    "เข้าต้น",
    "เข้าดอก",
    "ค่างวด",
    "ชำระแล้ว",
  ];
  const colWidths = [10, 20, 20, 24, 22, 22, 22, 22, 20];
  const colAligns = [
    "center",
    "center",
    "center",
    "center",
    "right",
    "right",
    "right",
    "right",
    "right",
  ];
  const colX = [mL];
  colWidths.forEach((w) => colX.push(colX[colX.length - 1] + w));
  const rowH = 7;

  const drawDocHeader = () => {
    setF("bold", 18);
    black();
    pdf.text("ตารางดิว (ทำยอม)", mL, 16);

    setF("normal", 13);
    pdf.text(`เลขที่สัญญา : ${str(contno)}`, mL, 23);
    pdf.text(`ชื่อลูกค้า : ${str(customerName)}`, mL, 29);
    pdf.text(`วันที่พิมพ์ : ${dayjs().format("DD/MM/YYYY")}`, xR, 23, {
      align: "right",
    });

    return 35;
  };

  const drawTableHeader = (y) => {
    setF("bold", 10);
    black();
    pdf.setFillColor(235, 235, 235);
    pdf.rect(mL, y, contentW, rowH, "F");
    box(mL, y, contentW, rowH);
    headers.forEach((h, i) => {
      pdf.line(colX[i], y, colX[i], y + rowH);
      pdf.text(h, colX[i] + colWidths[i] / 2, y + rowH / 2 + 1.5, {
        align: "center",
      });
    });
    pdf.line(xR, y, xR, y + rowH);
    return y + rowH;
  };

  let y = drawDocHeader();
  y = drawTableHeader(y);

  const ensureSpace = (needed) => {
    if (y + needed > bottomLimit) {
      pdf.addPage();
      y = 16;
      y = drawTableHeader(y);
    }
  };

  const drawCell = (i, text, color) => {
    pdf.line(colX[i], y, colX[i], y + rowH);
    pdf.setTextColor(...(color || [0, 0, 0]));
    const align = colAligns[i];
    const tx =
      align === "right"
        ? colX[i + 1] - 2
        : align === "center"
          ? colX[i] + colWidths[i] / 2
          : colX[i] + 2;
    pdf.text(text, tx, y + rowH / 2 + 1.5, { align });
  };

  const drawDueRow = (record) => {
    ensureSpace(rowH);
    box(mL, y, contentW, rowH);

    const status = getDueStatus(record);
    const amtColor = getDueAmtColor(record);
    const payColor = Number(record.PAYAMT) > 0 ? COLOR.success : COLOR.danger;

    setF("normal", 10);
    drawCell(0, str(record.NOPAY), [0, 0, 0]);
    drawCell(1, formatDate(record.DUEDATE), [0, 0, 0]);
    drawCell(2, formatDate(record.PAYDATE), [0, 0, 0]);
    setF("bold", 10);
    drawCell(3, status.text, status.color);
    setF("normal", 10);
    drawCell(4, formatMoney(record.TONBALANCE), [0, 0, 0]);
    drawCell(5, formatMoney(record.DUETONEFF), amtColor);
    drawCell(6, formatMoney(record.DUEINTEFF), amtColor);
    drawCell(7, formatMoney(record.DUEAMT), amtColor);
    drawCell(8, formatMoney(record.PAYAMT), payColor);

    pdf.line(xR, y, xR, y + rowH);
    black();
    y += rowH;
  };

  // แถวเบี้ยปรับ: จัดค่าให้ตรงคอลัมน์เดียวกับแถวดิว เพื่อให้อ่าน/เทียบเลขได้ง่ายขึ้น
  const drawPenaltyRow = (record) => {
    ensureSpace(rowH);
    pdf.setFillColor(255, 251, 230);
    pdf.rect(mL, y, contentW, rowH, "F");
    box(mL, y, contentW, rowH);

    const delayDay = Number(record.DELAYDAY || 0);
    const beeypup = Number(record.BEEYPUP || 0);
    const payBeeypup = Number(record.PAY_BEEYPUP || 0);
    const isPaid = payBeeypup > 0;
    const orange = COLOR.warningDark;

    // ป้าย "เบี้ยปรับ" คร่อมคอลัมน์งวดที่ + วันครบกำหนด (เหมือน colSpan ในหน้า DuePaymentTab)
    setF("bold", 12);
    pdf.setTextColor(...orange);
    pdf.text(
      `• เบี้ยปรับ งวดที่ ${record.NOPAY} (เกิน ${delayDay} วัน)`,
      mL + 3,
      y + rowH / 2 + 1.5,
    );

    // ค่าที่เหลือ จัดตำแหน่งตรงกับคอลัมน์: วันที่ชำระ / สถานะ / เงินต้นคงเหลือ / เข้าต้น / เข้าดอก / ค่างวด / ชำระแล้ว
    const vals = [
      isPaid ? formatDate(record.PAYDATE) : "-",
      `ค้าง ${delayDay} วัน`,
      "-",
      "-",
      "-",
      formatMoney(beeypup),
      formatMoney(payBeeypup),
    ];
    const lastCol = headers.length - 1;
    setF("normal", 10);
    for (let i = 2; i < headers.length; i += 1) {
      pdf.line(colX[i], y, colX[i], y + rowH);
      const color =
        i === lastCol ? (isPaid ? COLOR.success : COLOR.danger) : orange;
      pdf.setTextColor(...color);
      const align = colAligns[i];
      const tx =
        align === "right"
          ? colX[i + 1] - 2
          : align === "center"
            ? colX[i] + colWidths[i] / 2
            : colX[i] + 2;
      pdf.text(vals[i - 2], tx, y + rowH / 2 + 1.5, { align });
    }
    pdf.line(xR, y, xR, y + rowH);

    black();
    y += rowH;
  };

  rows.forEach((record) => {
    if (record.__rowType === "penalty") drawPenaltyRow(record);
    else drawDueRow(record);
  });

  // ── แถวสรุปยอด: ตรงกับ summary ของตารางในหน้า DuePaymentTab ──
  const dueRows = rows.filter((r) => r.__rowType !== "penalty");
  const penaltyRows = rows.filter((r) => r.__rowType === "penalty");
  const totalDue = dueRows.reduce((sum, r) => sum + Number(r.DUEAMT || 0), 0);
  const totalPaid = dueRows.reduce((sum, r) => sum + Number(r.PAYAMT || 0), 0);
  const totalTonEff = dueRows.reduce(
    (sum, r) => sum + Number(r.DUETONEFF || 0),
    0,
  );
  const totalIntEff = dueRows.reduce(
    (sum, r) => sum + Number(r.DUEINTEFF || 0),
    0,
  );
  const remaining = totalDue - totalPaid;
  const totalPenaltyCharged = penaltyRows.reduce(
    (sum, r) => sum + Number(r.BEEYPUP || 0),
    0,
  );
  const totalPenaltyPaid = penaltyRows.reduce(
    (sum, r) => sum + Number(r.PAY_BEEYPUP || 0),
    0,
  );
  const remainingPenalty = totalPenaltyCharged - totalPenaltyPaid;

  // แถว "รวมทั้งหมด": ป้ายคร่อมคอลัมน์ งวดที่..เงินต้นคงเหลือ (colSpan 5 เหมือนหน้า DuePaymentTab)
  const drawSummaryRowAll = () => {
    const h = rowH + 3;
    ensureSpace(h);
    pdf.setFillColor(235, 235, 235);
    pdf.rect(mL, y, contentW, h, "F");
    box(mL, y, contentW, h);
    [5, 6, 7, 8].forEach((i) => pdf.line(colX[i], y, colX[i], y + h));
    pdf.line(xR, y, xR, y + h);

    setF("bold", 12);
    black();
    pdf.text("รวมทั้งหมด", colX[0] + 3, y + h / 2 + 1);

    setF("normal", 10);
    black();
    pdf.text(formatMoney(totalTonEff), colX[6] - 2, y + h / 2 + 1, {
      align: "right",
    });
    pdf.text(formatMoney(totalIntEff), colX[7] - 2, y + h / 2 + 1, {
      align: "right",
    });

    setF("bold", 12);
    black();
    pdf.text(formatMoney(totalDue), colX[8] - 2, y + h / 2 + 1, {
      align: "right",
    });

    setF("bold", 12);
    pdf.setTextColor(...(totalPaid > 0 ? COLOR.success : COLOR.danger));
    pdf.text(formatMoney(totalPaid), xR - 2, y + h / 2 - 1, {
      align: "right",
    });
    setF("normal", 9);
    pdf.setTextColor(120, 120, 120);
    pdf.text(`คงเหลือ ${formatMoney(remaining)}`, xR - 2, y + h / 2 + 4, {
      align: "right",
    });

    black();
    y += h;
  };

  // แถว "รวมเบี้ยปรับ": ป้ายคร่อมคอลัมน์ งวดที่..เข้าต้น (colSpan 6) แล้วแยก 3 ช่อง คงเหลือ/ถูกคิด/ชำระแล้ว เหมือนหน้า DuePaymentTab
  const drawSummaryRowPenalty = () => {
    const h = rowH + 3;
    ensureSpace(h);
    pdf.setFillColor(255, 251, 230);
    pdf.rect(mL, y, contentW, h, "F");
    box(mL, y, contentW, h);
    [6, 7, 8].forEach((i) => pdf.line(colX[i], y, colX[i], y + h));
    pdf.line(xR, y, xR, y + h);

    const orange = COLOR.warningDark;
    setF("bold", 12);
    pdf.setTextColor(...orange);
    pdf.text("รวมเบี้ยปรับ", colX[0] + 3, y + h / 2 + 1);

    setF("bold", 12);
    pdf.setTextColor(...(remainingPenalty > 0 ? COLOR.danger : COLOR.success));
    pdf.text(formatMoney(remainingPenalty), colX[7] - 2, y + h / 2 - 1, {
      align: "right",
    });
    setF("normal", 9);
    pdf.setTextColor(120, 120, 120);
    pdf.text("คงเหลือ", colX[7] - 2, y + h / 2 + 4, { align: "right" });

    setF("bold", 12);
    pdf.setTextColor(...orange);
    pdf.text(formatMoney(totalPenaltyCharged), colX[8] - 2, y + h / 2 - 1, {
      align: "right",
    });
    setF("normal", 9);
    pdf.setTextColor(120, 120, 120);
    pdf.text("ถูกคิด", colX[8] - 2, y + h / 2 + 4, { align: "right" });

    setF("bold", 12);
    pdf.setTextColor(
      ...(totalPenaltyPaid > 0 ? COLOR.success : COLOR.danger),
    );
    pdf.text(formatMoney(totalPenaltyPaid), xR - 2, y + h / 2 - 1, {
      align: "right",
    });
    setF("normal", 9);
    pdf.setTextColor(120, 120, 120);
    pdf.text("ชำระแล้ว", xR - 2, y + h / 2 + 4, { align: "right" });

    black();
    y += h;
  };

  drawSummaryRowAll();
  drawSummaryRowPenalty();

  if (!openWindow) return pdf;

  // ── เปิด / สั่งพิมพ์ ──
  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  const win = window.open(url);
  if (win) win.onload = () => win.print();
  else alert("กรุณาปิดการบล็อกป๊อปอัปเพื่อใช้งานฟังก์ชันนี้");
};

export default exportDuePaymentPdf;
